import { ArrowBack } from "@mui/icons-material";
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import GroupCreditsPoolPanel from "@/components/usersOrgs/GroupCreditsPoolPanel";
import GroupInvoicesPanel from "@/components/usersOrgs/GroupInvoicesPanel";
import GroupPaymentMethodsPanel from "@/components/usersOrgs/GroupPaymentMethodsPanel";
import GroupTransactionsPanel from "@/components/usersOrgs/GroupTransactionsPanel";
import GroupUsersPanel from "@/components/usersOrgs/GroupUsersPanel";
import TabButton from "@/components/usersOrgs/TabButton";
import { useUpdateGroup } from "@/hooks/useGroups";
import { usePermissions } from "@/hooks/usePermissions";
import type { AccountDeepLink } from "@/interfaces/accountNav";
import type { GroupNode } from "@/interfaces/group";

type GroupTab =
  | "details"
  | "users"
  | "credits-pool"
  | "transactions"
  | "payment-methods"
  | "invoices";
const TABS: { key: GroupTab; label: string }[] = [
  { key: "details", label: "Details" },
  { key: "users", label: "Users" },
  { key: "credits-pool", label: "Wallet" },
  { key: "transactions", label: "Transactions" },
  { key: "payment-methods", label: "Payment Methods" },
  { key: "invoices", label: "Invoices" },
];
// The credit surfaces (Wallets, transactions, cards) require CREDIT/view — a view-only client
// role sees them, a role without CREDIT does not. Details/Users are ungated here.
const CREDIT_TABS = new Set<GroupTab>(["credits-pool", "transactions", "payment-methods"]);

const cardSx = {
  bgcolor: "var(--color-white)",
  border: "1px solid var(--color-smoke)",
  borderRadius: "12px",
  p: 3,
  maxWidth: 560,
} as const;

/**
 * A group's Details tab — name + credit-purchase lever. Edits are local until saved;
 * "Save changes" only enables once something actually changed, and Cancel reverts to the
 * last saved values. A root group's credit lever is locked on (root must always purchase).
 */
function DetailsPanel({ group, organizationId }: { group: GroupNode; organizationId: number }) {
  const updateGroup = useUpdateGroup(organizationId);
  const isRoot = group.parentId === null;
  const savedCredit = isRoot ? true : group.creditPurchaseAllowed;

  const [name, setName] = useState(group.name);
  const [credit, setCredit] = useState(savedCredit);
  const [error, setError] = useState<string | null>(null);

  // Re-sync when the underlying group changes (after a save refetch, or a different group).
  useEffect(() => {
    setName(group.name);
    setCredit(isRoot ? true : group.creditPurchaseAllowed);
    setError(null);
  }, [group.name, group.creditPurchaseAllowed, isRoot]);

  const dirty = name !== group.name || credit !== savedCredit;
  const canSave = dirty && name.trim().length > 0 && !updateGroup.isPending;

  function handleSave() {
    setError(null);
    const patch: { name?: string; creditPurchaseAllowed?: boolean } = {};
    if (name.trim() !== group.name) patch.name = name.trim();
    if (credit !== savedCredit) patch.creditPurchaseAllowed = credit;
    if (Object.keys(patch).length === 0) return;

    updateGroup.mutate(
      { id: group.id, patch },
      { onError: (e) => setError(e instanceof Error ? e.message : "Failed to save.") },
    );
  }

  function handleCancel() {
    setName(group.name);
    setCredit(savedCredit);
    setError(null);
  }

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          label="Group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={credit}
              disabled={isRoot}
              onChange={(e) => setCredit(e.target.checked)}
              size="small"
            />
          }
          label={isRoot ? "Can purchase credit (root groups always can)" : "Can purchase credit"}
          slotProps={{ typography: { sx: { fontSize: 14 } } }}
        />
        {error && <Typography sx={{ fontSize: 13, color: "error.main" }}>{error}</Typography>}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
          <Button
            onClick={handleCancel}
            disabled={!dirty || updateGroup.isPending}
            sx={{ color: "var(--color-graphite)" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!canSave}
            sx={{ bgcolor: "var(--color-rodeo-black)" }}
          >
            {updateGroup.isPending ? "Saving…" : "Save changes"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

/** A single group's detail view — replaces the hierarchy table while open. */
export default function GroupView({
  group,
  organizationId,
  onBack,
  deepLink,
}: {
  group: GroupNode;
  organizationId: number;
  onBack: () => void;
  /**
   * One-shot deep-link message, forwarded by GroupTree; only its `groupTab` (and the `nonce`
   * that dates it) matter here. Untyped on the wire, so the tab name is validated below against
   * the tabs this caller can actually see — an unknown or gated name falls back to Details.
   */
  deepLink?: AccountDeepLink | null;
}) {
  const [tab, setTab] = useState<GroupTab>("details");
  // Root groups always may purchase; others only if the flag is set. Gates the Buy Credits and
  // Add-card actions (existing cards/credits stay visible if the flag is later turned off).
  const canPurchase = group.parentId === null || group.creditPurchaseAllowed;
  // Credit surfaces (wallet/transactions/cards) require CREDIT/view; the Invoices tab is GFG-only
  // (CREDIT/send_invoice). Hide those tabs otherwise.
  const { can } = usePermissions();
  const canViewCredit = can("view", "CREDIT");
  const canSendInvoice = can("send_invoice", "CREDIT");
  const visibleTabs = TABS.filter((t) => {
    if (t.key === "invoices") return canSendInvoice;
    if (CREDIT_TABS.has(t.key)) return canViewCredit;
    return true;
  });

  // Resolving through the filtered list both validates the deep link and narrows it to a
  // GroupTab. Sync rather than initial-value-only, and keyed on the message's `nonce` as well as
  // its target, so a second deep link moves the tab even when it asks for the SAME tab; a manual
  // tab click, which carries no nonce, is left alone.
  const requestedTab = visibleTabs.find((t) => t.key === deepLink?.groupTab)?.key ?? null;
  const nonce = deepLink?.nonce;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `nonce` is deliberately a dependency the body doesn't read — it is the only thing that distinguishes a repeat deep link to the tab already requested, which the target value alone cannot.
  useEffect(() => {
    if (requestedTab) setTab(requestedTab);
  }, [nonce, requestedTab]);

  return (
    <Box>
      {/* Back to the group hierarchy — mirrors OrgDetail's "← Organizations". */}
      <button
        type="button"
        onClick={onBack}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          border: "none",
          background: "none",
          cursor: "pointer",
          color: "var(--color-graphite)",
          fontFamily: "var(--font-lato)",
          fontSize: 14,
          padding: 0,
          marginBottom: 12,
        }}
      >
        <ArrowBack sx={{ fontSize: 18 }} /> Groups
      </button>

      <Box
        component="h2"
        sx={{
          m: 0,
          mb: 2,
          fontFamily: "var(--font-crimson)",
          fontSize: 26,
          fontWeight: 400,
          lineHeight: 1.2,
          color: "var(--color-rodeo-black)",
        }}
      >
        {group.name}
      </Box>

      <div
        role="tablist"
        style={{
          display: "flex",
          gap: 24,
          borderBottom: "1px solid var(--color-card-border)",
          marginBottom: 24,
        }}
      >
        {visibleTabs.map((t) => (
          <TabButton
            key={t.key}
            active={tab === t.key}
            label={t.label}
            onClick={() => setTab(t.key)}
          />
        ))}
      </div>

      {tab === "details" && <DetailsPanel group={group} organizationId={organizationId} />}
      {tab === "users" && (
        <GroupUsersPanel
          group={{ id: group.id, name: group.name }}
          organizationId={organizationId}
        />
      )}
      {tab === "credits-pool" && (
        <GroupCreditsPoolPanel
          groupId={group.id}
          groupName={group.name}
          organizationId={organizationId}
          canPurchase={canPurchase}
          // A group with no card cannot buy, and the remedy lives on another tab. Rather than
          // disable the button and leave the client to find it, the button takes them there.
          onAddPaymentMethod={() => setTab("payment-methods")}
        />
      )}
      {tab === "transactions" && <GroupTransactionsPanel groupId={group.id} />}
      {tab === "payment-methods" && (
        <GroupPaymentMethodsPanel groupId={group.id} canPurchase={canPurchase} />
      )}
      {tab === "invoices" && <GroupInvoicesPanel groupId={group.id} />}
    </Box>
  );
}
